import React, { useState, useEffect } from "react";
import "./DeleteAccountPopup.css";
import APIURL from "../../common/Config";
import { useNavigate } from "react-router-dom";
import { sendOtp, validateOtp } from "../../utils/otp-service";
import Footer from "../footer/footer";
import Header from "../header/header";

const WarningIcon = "⚠️";
const InfoIcon = "ℹ️";
const ClockIcon = "🕑";
const TrashIcon = "🗑️";

const Step1 = ({
  mobile,
  setMobile,
  handleSendOtp,
  loading,
  message,
  isChecked,
  setIsChecked,
}) => (
  <>
    <div className="da-warning-box">
      <div className="da-warning-content">
        <span className="da-warning-icon">{WarningIcon}</span>
        <p>
          Deleting your account will permanently remove all your data, including
          saved preferences, history, and any associated content. This action
          cannot be reversed.
        </p>
      </div>
    </div>
    <div className="da-form-group">
      <p className="da-input-label">
        Enter your mobile number to receive a verification OTP for account
        deletion.
      </p>
      <div className="da-input-wrapper">
        <span className="da-country-code">+91</span>
        <input
          className="da-input"
          type="tel"
          placeholder=""
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          maxLength={10}
        />
      </div>
    </div>
    <div className="da-checkbox-group">
      <input
        type="checkbox"
        id="confirm-deletion"
        checked={isChecked}
        onChange={(e) => setIsChecked(e.target.checked)}
        className="da-checkbox"
      />
      <label htmlFor="confirm-deletion" className="da-checkbox-label">
        I understand that all my data will be permanently deleted
      </label>
    </div>
    <button
      className={`da-button da-send-code ${
        !isChecked || loading ? "da-button-disabled" : ""
      }`}
      onClick={handleSendOtp}
      disabled={loading || !isChecked}
      style={{ backgroundColor: isChecked ? "#89184A" : "#D1D5DB" }}
    >
      {loading ? "Sending OTP..." : "Send Verification Code"}
    </button>
    {message && (
      <p
        className={`da-message ${
          message.includes("Success")
            ? "da-message-success"
            : "da-message-error"
        }`}
      >
        {message}
      </p>
    )}
  </>
);

const Step2 = ({
  otp,
  setOtp,
  handleValidateOtp,
  loading,
  message,
  mobile,
  handleResendOtp,
}) => {
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const onResendClick = () => {
    setCanResend(false);
    setResendTimer(60);
    handleResendOtp();
  };

  const getMaskedMobile = (number) => {
    if (!number) return "";
    return `+91 ******-${number.slice(6)}`;
  };

  return (
    <div>
      <div className="da-warning-box">
        <div className="da-warning-content">
          <span className="da-warning-icon">{WarningIcon}</span>
          <p>
            Deleting your account will permanently remove all your data,
            including saved preferences, history, and any associated content.
            This action cannot be reversed.
          </p>
        </div>
      </div>
      <div className="da-form-group">
        <p className="da-input-label">Enter Verification Code</p>
        <p className="da-otp-sent-info">
          We've sent a 4-digit OTP to {getMaskedMobile(mobile)}
        </p>
        <div className="da-otp-input-container">
          {Array(4)
            .fill()
            .map((_, index) => (
              <input
                key={index}
                className={`da-otp-input ${
                  otp[index] ? "da-otp-input-filled" : ""
                }`}
                type="tel"
                maxLength="1"
                value={otp[index] || ""}
                onChange={(e) => {
                  const newValue = e.target.value.slice(-1);
                  const newOtpArray = otp.split("");
                  newOtpArray[index] = newValue;
                  setOtp(newOtpArray.join(""));

                  if (newValue && index < 3) {
                    e.target.nextSibling?.focus();
                  } else if (!newValue && index > 0) {
                    e.target.previousSibling?.focus();
                  }
                }}
              />
            ))}
        </div>
      </div>
      <div className="da-resend-area">
        <button
          onClick={onResendClick}
          disabled={!canResend}
          className={`da-resend-btn ${canResend ? "da-resend-active" : ""}`}
        >
          Didn't receive code? Resend
        </button>
        {!canResend && (
          <p className="da-resend-timer">
            Resend available in 00:
            {resendTimer < 10 ? `0${resendTimer}` : resendTimer}
          </p>
        )}
      </div>
      <button
        className={`da-button da-verify-delete ${
          loading || otp.length < 4 ? "da-button-disabled-red" : ""
        }`}
        onClick={handleValidateOtp}
        disabled={loading || otp.length < 4}
      >
        {loading ? "Validating..." : "Verify & Delete Account"}
      </button>
      {message && (
        <p
          className={`da-message ${
            message.includes("Success")
              ? "da-message-success"
              : "da-message-error"
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
};

const Step3 = ({ navigate }) => (
  <div>
    <div className="da-success-icon-wrapper">
      <div className="da-success-icon-bg">
        <span className="da-success-icon">{TrashIcon}</span>
      </div>
    </div>
    <h2 className="da-success-title">Account Deleted</h2>
    <p className="da-success-subtitle">
      Your account has been successfully deleted.
    </p>
    <div className="da-info-box">
      <div className="da-info-item">
        <span className="da-info-icon">{InfoIcon}</span>
        <p>
          All your personal data has been permanently removed from our systems.
        </p>
      </div>
      <div className="da-info-item">
        <span className="da-info-icon">{ClockIcon}</span>
        <p>Some cached data may take up to 24 hours to disappear completely.</p>
      </div>
    </div>
    <button className="da-button da-return-home" onClick={() => navigate("/")}>
      <span className="da-home-icon">🏠</span> Return to Homepage
    </button>
  </div>
);

const DeleteAccountPopup = ({ onClose }) => {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSessionId, setOtpSessionId] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isChecked, setIsChecked] = useState(false);

  const navigate = useNavigate();

  const handleSendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      setMessage("Please enter a valid mobile number.");
      return;
    }
    if (!isChecked) {
      setMessage("Please confirm that you understand the deletion process.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const response = await sendOtp(mobile);
      if (response?.status === "SUCCESS") {
        setOtpSessionId(response.otpSessionId);
        setStep(2);
        setMessage("OTP Sent Successfully!");
      } else {
        setMessage(response?.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error("Send OTP Error:", error);
      setMessage("Error sending OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setMessage("");
    try {
      const response = await sendOtp(mobile);
      if (response?.status === "SUCCESS") {
        setOtpSessionId(response.otpSessionId);
        setMessage("OTP Re-sent Successfully!");
      } else {
        setMessage(response?.message || "Failed to re-send OTP.");
      }
    } catch (error) {
      console.error("Re-send OTP Error:", error);
      setMessage("Error re-sending OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOtp = async () => {
    if (!otp || otp.length !== 4 || !otpSessionId || !mobile) {
      setMessage("Invalid OTP or missing information.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const response = await validateOtp(mobile, otp, otpSessionId);
      if (response?.status === "SUCCESS") {
        await deleteAccount();
      } else {
        setMessage(response?.message || "Invalid OTP.");
      }
    } catch (error) {
      console.error("Validate OTP Error:", error);
      setMessage("Error validating OTP.");
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    const url = `${APIURL}/astroservice/delete/customer/${mobile}`;
    setLoading(true);
    setMessage("");
    try {
      await fetch(url, { method: "DELETE" });
      setStep(3);
      setMessage("Account deleted successfully!");
    } catch (error) {
      console.error("Delete Account Error:", error);
      setMessage("Failed to delete account.");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1
            {...{
              mobile,
              setMobile,
              handleSendOtp,
              loading,
              message,
              isChecked,
              setIsChecked,
            }}
          />
        );
      case 2:
        return (
          <Step2
            {...{
              otp,
              setOtp,
              handleValidateOtp,
              loading,
              message,
              mobile,
              handleResendOtp,
            }}
          />
        );
      case 3:
        return <Step3 navigate={navigate} />;
      default:
        return null;
    }
  };

  return (
  <div className="delete-account-page">
    <Header />
        <div className="da-page-container">
        <div className="da-container">
          {step !== 3 && (
            <div className="da-header">
              <h2 className="da-title">Secure Account Deletion</h2>
              <div className="da-alert-icon-wrapper">
                <span className="da-alert-icon">{WarningIcon}</span>
              </div>
              <button className="da-close-btn" onClick={onClose}>
                ×
              </button>
            </div>
          )}

          <div className="da-content">{renderStep()}</div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DeleteAccountPopup;
